import React from "react";
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Box,
    Stack,
    Chip,
    Divider,
    Tooltip,
    Button,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// If you already have ReportedProgress from earlier, import it:
// import ReportedProgress from "./ReportedProgress";

function formatMonthShort(dateLike) {
    const d = new Date(dateLike);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function usageColor(used, limit) {
    if (!limit || limit <= 0) return "default";
    const pct = (used / limit) * 100;
    if (pct >= 100) return "error";
    if (pct >= 80) return "warning";
    return "success";
}

export default function OrganisationUsageSection({ user, onUpgrade }) {
    if (!user || !user.organisation) return null;

    const org = user.organisation;

    const reportItems = Number(org.current_report_entries_month || 0);
    const reportLimit = Number(org.max_report_entries_per_month || 0);
    const reportDate = formatMonthShort(org.current_month);

    const projUsed = Number(org.project_count || 0);
    const projLimit = Number(org.project_limit || 0);
    const projColor = usageColor(projUsed, projLimit);

    const brandUsed = Number(org.brand_count || 0);
    const brandLimit = Number(org.brand_limit || 0);
    const brandColor = usageColor(brandUsed, brandLimit);

    const atOrOverProject = projLimit > 0 && projUsed >= projLimit;
    const atOrOverBrand = brandLimit > 0 && brandUsed >= brandLimit;

    return (
        <Card sx={{ borderRadius: 3 }}>
            <CardHeader
                title="Usage this month"
                subheader={
                    reportDate ? `Since ${reportDate}` : undefined
                }
                sx={{ pb: 0 }}
            />
            <CardContent>
                {/* Reports (keep as progress) */}
                <Box sx={{ mb: 2 }}>
                    {/* If you have the ReportedProgress component from earlier, use it: */}
                    {/* <ReportedProgress items={reportItems} limit={reportLimit} date={org.current_month} /> */}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Reported {reportItems} from {reportLimit} since {reportDate}
                    </Typography>
                    {/* Lightweight inlined progress (kept for reports only) */}
                    <Box
                        aria-label="Reports usage progress"
                        role="progressbar"
                        sx={{
                            height: 10,
                            width: "100%",
                            borderRadius: 999,
                            backgroundColor: (t) => t.palette.action.hover,
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                height: "100%",
                                width: `${Math.min(
                                    reportLimit ? (reportItems / reportLimit) * 100 : 0,
                                    100
                                )}%`,
                                backgroundColor: (t) => t.palette.primary.main,
                            }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Projects & Brands — NOT progress bars */}
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    useFlexGap
                    flexWrap="wrap"
                    alignItems="stretch"
                >
                    {/* Projects */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 260,
                            p: 2,
                            borderRadius: 2,
                            border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <FolderIcon fontSize="small" />
                            <Typography variant="subtitle2">Projects</Typography>
                            <Tooltip title="How many projects your organisation can create.">
                                <InfoOutlinedIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                            </Tooltip>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                                label={`${projUsed} / ${projLimit || "—"}`}
                                color={projColor}
                                size="small"
                                variant="filled"
                            />
                            <Chip
                                label={
                                    projLimit
                                        ? `${Math.max(projLimit - projUsed, 0)} remaining`
                                        : "No limit"
                                }
                                size="small"
                                variant="outlined"
                            />
                        </Stack>

                        {atOrOverProject && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="caption" color="error">
                                    You’ve reached your project limit.
                                </Typography>
                                {onUpgrade && (
                                    <Button
                                        size="small"
                                        sx={{ ml: 1 }}
                                        onClick={onUpgrade}
                                    >
                                        Upgrade
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Brands */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 260,
                            p: 2,
                            borderRadius: 2,
                            border: (t) => `1px solid ${t.palette.divider}`,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <BrandingWatermarkIcon fontSize="small" />
                            <Typography variant="subtitle2">Brands</Typography>
                            <Tooltip title="How many brands your organisation can manage.">
                                <InfoOutlinedIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                            </Tooltip>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                                label={`${brandUsed} / ${brandLimit || "—"}`}
                                color={brandColor}
                                size="small"
                                variant="filled"
                            />
                            <Chip
                                label={
                                    brandLimit
                                        ? `${Math.max(brandLimit - brandUsed, 0)} remaining`
                                        : "No limit"
                                }
                                size="small"
                                variant="outlined"
                            />
                        </Stack>

                        {atOrOverBrand && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="caption" color="error">
                                    You’ve reached your brand limit.
                                </Typography>
                                {onUpgrade && (
                                    <Button
                                        size="small"
                                        sx={{ ml: 1 }}
                                        onClick={onUpgrade}
                                    >
                                        Upgrade
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}
